import { useEffect, useState } from 'react';
import { fetchOgPreview, type OgPreview } from '@/lib/http/og';

export interface UseOgPreviewResult {
  data: OgPreview | null;
  loading: boolean;
  /** True if the fetch threw (network/server error). Distinct from a cached
   *  'failed' status, which resolves successfully with status:'failed'. */
  error: boolean;
}

/**
 * Resolves OpenGraph metadata for `href` via the server (cached). Fetches once
 * per href; re-fetches when href changes. Empty href is a no-op (no request).
 * The server endpoint caches per-URL, so the second viewer of a link gets it
 * instantly.
 */
export function useOgPreview(href: string): UseOgPreviewResult {
  const [data, setData] = useState<OgPreview | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(href));
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!href) {
      setData(null);
      setLoading(false);
      setError(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchOgPreview(href)
      .then((preview) => {
        if (cancelled) return;
        setData(preview);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [href]);

  return { data, loading, error };
}
